import { trailUpdate, trailCreate, TRAIL_MODULES } from '../../utils/activityTrail';
import React, { useState, useEffect, useRef } from 'react';
import {
    Card, Button, Table, Tag, message, Modal, Input,
    Row, Col, Tooltip, Alert, Progress, Badge, Select, Spin,
} from 'antd';
import {
    EnvironmentOutlined, AimOutlined, EditOutlined,
    QrcodeOutlined, CheckCircleOutlined, WarningOutlined,
    ReloadOutlined, GlobalOutlined, PrinterOutlined,
    CloudDownloadOutlined, DownloadOutlined,
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import util from '../../utils/util';
import coa  from '../assets/footer/coa.png';
import flag from '../assets/footer/flag.png';
import FacilityService from '../../services/FacilityService';

import { SITE_TYPES as FACILITY_LEVELS } from '../../constants/facilityTypes';

// ─── Haversine (for accuracy display) ───────────────────────────────────────
function metres(lat1, lon1, lat2, lon2) {
    const R = 6371000, r = x => x * Math.PI / 180;
    const dL = r(lat2 - lat1), dN = r(lon2 - lon1);
    const a = Math.sin(dL/2)**2 + Math.cos(r(lat1))*Math.cos(r(lat2))*Math.sin(dN/2)**2;
    return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
}

// ─── Google Static Map thumbnail ────────────────────────────────────────────
function MapThumb({ lat, lng, size = 120 }) {
    if (!lat || !lng) return null;
    // Using OpenStreetMap tile for zero-cost static preview
    const osmUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(lng)-0.002},${parseFloat(lat)-0.002},${parseFloat(lng)+0.002},${parseFloat(lat)+0.002}&layer=mapnik&marker=${lat},${lng}`;
    return (
        <a href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`}
            target="_blank" rel="noreferrer"
            style={{ display:'block', width:size, height:size, borderRadius:8,
                overflow:'hidden', border:'1px solid #e8e8e8', flexShrink:0 }}>
            <iframe
                src={osmUrl}
                width={size} height={size}
                style={{ border:'none', pointerEvents:'none', transform:'scale(1)', transformOrigin:'0 0' }}
                title="map"
            />
        </a>
    );
}

// ─── Live GPS capture modal ──────────────────────────────────────────────────
function LiveGpsModal({ facility, onSave, onClose }) {
    const [watching,  setWatching]  = useState(false);
    const [position,  setPosition]  = useState(null);
    const [accuracy,  setAccuracy]  = useState(null);
    const [error,     setError]     = useState(null);
    const [samples,   setSamples]   = useState([]);
    const [saving,    setSaving]    = useState(false);
    const watchId = useRef(null);

    const stopWatch = () => {
        if (watchId.current !== null) {
            navigator.geolocation.clearWatch(watchId.current);
            watchId.current = null;
        }
        setWatching(false);
    };

    const startCapture = () => {
        if (!navigator.geolocation) {
            setError('Geolocation is not supported on this device.');
            return;
        }
        setError(null);
        setSamples([]);
        setPosition(null);
        setWatching(true);

        watchId.current = navigator.geolocation.watchPosition(
            (pos) => {
                const { latitude, longitude, accuracy: acc } = pos.coords;
                setSamples(prev => {
                    const next = [...prev, { lat: latitude, lng: longitude, acc }].slice(-10);
                    // Average the last N readings for better accuracy
                    const avgLat = next.reduce((s, p) => s + p.lat, 0) / next.length;
                    const avgLng = next.reduce((s, p) => s + p.lng, 0) / next.length;
                    const bestAcc = Math.min(...next.map(p => p.acc));
                    setPosition({ lat: avgLat.toFixed(7), lng: avgLng.toFixed(7) });
                    setAccuracy(Math.round(bestAcc));
                    return next;
                });
            },
            (err) => {
                setError(err.code === 1
                    ? 'Location access denied. Please allow location in browser settings.'
                    : 'Unable to get location. Make sure GPS is enabled.');
                setWatching(false);
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
    };

    useEffect(() => () => stopWatch(), []);

    const handleSave = async () => {
        if (!position) return;
        setSaving(true);
        try {
            await QoCService.saveFacilityCoordinates(facility.id, {
                lat: position.lat, lng: position.lng,
                coord_source: 'live_gps', coord_accuracy: accuracy,
            });
             trailUpdate(TRAIL_MODULES.FACILITIES, facility.business_name, facility.id,
                { lat: position.lat, lng: position.lng, source: 'live_gps', accuracy: `±${accuracy}m` });
            message.success('Coordinates saved from live GPS');
            onSave();
        } catch { message.error('Save failed'); }
        finally { setSaving(false); }
    };

    const accuracyColor = !accuracy ? '#888'
        : accuracy <= 10 ? '#52c41a'
        : accuracy <= 25 ? '#faad14'
        : '#f5222d';

    return (
        <Modal open onCancel={() => { stopWatch(); onClose(); }}
            title={<span><AimOutlined style={{ marginRight:8, color:'#1890ff' }}/>
                Live GPS — {facility?.business_name}</span>}
            footer={null} width={480}>

            {/* Instructions */}
            <Alert style={{ marginBottom:16 }} type="info" showIcon
                message="Stand at the main entrance of the facility"
                description="Hold this device steady outdoors for best accuracy. The app averages multiple readings to improve precision."/>

            {error && <Alert type="error" message={error} style={{ marginBottom:12 }} showIcon/>}

            {/* Capture button */}
            {!watching && !position && (
                <Button type="primary" icon={<AimOutlined/>} block size="large"
                    onClick={startCapture} style={{ marginBottom:16 }}>
                    Start GPS Capture
                </Button>
            )}

            {/* Live readings */}
            {watching && (
                <div style={{ textAlign:'center', padding:'16px 0', marginBottom:16 }}>
                    <div style={{ fontSize:12, color:'#888', marginBottom:8 }}>
                        Acquiring GPS signal... ({samples.length} readings)
                    </div>
                    <Progress type="circle" percent={Math.min(samples.length * 10, 100)}
                        width={80} strokeColor={accuracyColor}
                        format={() => <span style={{ fontSize:11 }}>{samples.length}/10</span>}/>
                    <div style={{ marginTop:12 }}>
                        <Button onClick={stopWatch} size="small">Stop</Button>
                    </div>
                </div>
            )}

            {/* Result */}
            {position && (
                <div style={{ marginBottom:16 }}>
                    <div style={{ background:'#f6ffed', border:'1px solid #b7eb8f',
                        borderRadius:8, padding:16, marginBottom:12 }}>
                        <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                            <div style={{ flex:1 }}>
                                <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>COORDINATES (averaged from {samples.length} readings)</div>
                                <div style={{ fontFamily:'monospace', fontSize:15, fontWeight:700, color:'#389e0d' }}>
                                    {position.lat}, {position.lng}
                                </div>
                            </div>
                            <div style={{ textAlign:'center' }}>
                                <div style={{ fontSize:11, color:'#888' }}>ACCURACY</div>
                                <div style={{ fontSize:20, fontWeight:800, color:accuracyColor }}>
                                    ±{accuracy}m
                                </div>
                            </div>
                        </div>
                        {accuracy > 25 && (
                            <div style={{ fontSize:11, color:'#fa8c16', marginTop:8 }}>
                                ⚠ Accuracy is low. Try moving outside or waiting for more readings.
                            </div>
                        )}
                    </div>
                    <MapThumb lat={position.lat} lng={position.lng} size={440}/>
                    <div style={{ display:'flex', gap:8, marginTop:12 }}>
                        <Button onClick={startCapture} icon={<ReloadOutlined/>}>Recapture</Button>
                        <Button type="primary" icon={<CheckCircleOutlined/>}
                            loading={saving} onClick={handleSave}
                            style={{ flex:1 }} disabled={accuracy > 50}>
                            Save Coordinates
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
}

// ─── Manual entry modal ──────────────────────────────────────────────────────
function ManualModal({ facility, onSave, onClose }) {
    const [lat,     setLat]     = useState(facility?.lat  || '');
    const [lng,     setLng]     = useState(facility?.lng  || '');
    const [saving,  setSaving]  = useState(false);
    const [preview, setPreview] = useState(
        facility?.lat ? { lat: facility.lat, lng: facility.lng } : null
    );

    const parsePaste = (text) => {
        // Handle "lat, lng" format from Google Maps
        const clean = text.replace(/[()]/g, '').trim();
        const parts = clean.split(/[\s,]+/);
        if (parts.length >= 2) {
            const a = parseFloat(parts[0]), b = parseFloat(parts[1]);
            if (!isNaN(a) && !isNaN(b)) {
                setLat(a.toFixed(7)); setLng(b.toFixed(7));
                setPreview({ lat: a.toFixed(7), lng: b.toFixed(7) });
            }
        }
    };

    const handleSave = async () => {
        const la = parseFloat(lat), ln = parseFloat(lng);
        if (isNaN(la) || isNaN(ln)) { message.error('Invalid coordinates'); return; }
        if (la < -90 || la > 90)    { message.error('Latitude must be between -90 and 90'); return; }
        if (ln < -180 || ln > 180)  { message.error('Longitude must be between -180 and 180'); return; }
        setSaving(true);
        try {
            await QoCService.saveFacilityCoordinates(facility.id, {
                lat: la.toFixed(7), lng: ln.toFixed(7),
                coord_source: 'manual',
            });
             trailUpdate(TRAIL_MODULES.FACILITIES, facility.business_name, facility.id,
                { lat: la.toFixed(7), lng: ln.toFixed(7), source: 'manual' });
            message.success('Coordinates saved');
            onSave();
        } catch { message.error('Save failed'); }
        finally { setSaving(false); }
    };

    return (
        <Modal open onCancel={onClose}
            title={<span><EditOutlined style={{ marginRight:8 }}/>
                Manual Entry — {facility?.business_name}</span>}
            footer={null} width={480}>

            <Alert style={{ marginBottom:16 }} type="info" showIcon
                message="Paste coordinates from Google Maps"
                description={<span>
                    Open Google Maps → right-click the facility entrance → click the coordinates
                    at the top of the menu to copy them, then paste below.
                </span>}/>

            <div style={{ marginBottom:12 }}>
                <div style={{ fontWeight:600, fontSize:12, marginBottom:4 }}>
                    Paste from Google Maps (e.g. -26.3167, 31.1333)
                </div>
                <Input.TextArea rows={2}
                    placeholder="-26.3167, 31.1333"
                    onChange={e => parsePaste(e.target.value)}
                    style={{ fontFamily:'monospace' }}/>
            </div>

            <Row gutter={12} style={{ marginBottom:12 }}>
                <Col span={12}>
                    <div style={{ fontWeight:600, fontSize:12, marginBottom:4 }}>Latitude</div>
                    <Input value={lat} onChange={e => { setLat(e.target.value); setPreview(null); }}
                        placeholder="-26.3167" style={{ fontFamily:'monospace' }}/>
                </Col>
                <Col span={12}>
                    <div style={{ fontWeight:600, fontSize:12, marginBottom:4 }}>Longitude</div>
                    <Input value={lng} onChange={e => { setLng(e.target.value); setPreview(null); }}
                        placeholder="31.1333" style={{ fontFamily:'monospace' }}/>
                </Col>
            </Row>

            {preview && <MapThumb lat={preview.lat} lng={preview.lng} size={440}/>}

            <div style={{ display:'flex', gap:8, marginTop:12 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button type="primary" icon={<CheckCircleOutlined/>}
                    loading={saving} onClick={handleSave} style={{ flex:1 }}
                    disabled={!lat || !lng}>
                    Save Coordinates
                </Button>
            </div>
        </Modal>
    );
}

// ─── MFL/HIE pull modal ──────────────────────────────────────────────────────
function MflModal({ facilities, onSave, onClose }) {
    const [loading,  setLoading]  = useState(false);
    const [results,  setResults]  = useState(null);
    const [saving,   setSaving]   = useState(false);
    const [mflUrl,   setMflUrl]   = useState('https://mfl.health.gov.sz/api/facilities');
    const [apiKey,   setApiKey]   = useState('');

    const pullFromMfl = async () => {
        setLoading(true);
        setResults(null);
        try {
            const res = await QoCService.pullCoordinatesFromMfl({ mfl_url: mflUrl, api_key: apiKey });
            setResults(res.data);
        } catch (err) {
            message.error('MFL pull failed: ' + (err?.response?.data?.message || err.message));
        } finally { setLoading(false); }
    };

    const applyAll = async () => {
        if (!results?.matched?.length) return;
        setSaving(true);
        try {
            await QoCService.applyMflCoordinates(results.matched);
            trailCreate(TRAIL_MODULES.FACILITIES, 'MFL Coordinate Sync', '',
                { facilityCount: results.matched.length, source: 'mfl' });
            message.success(`Updated ${results.matched.length} facilities from MFL`);
            onSave();
        } catch { message.error('Apply failed'); }
        finally { setSaving(false); }
    };

    return (
        <Modal open onCancel={onClose}
            title={<span><GlobalOutlined style={{ marginRight:8, color:'#722ed1' }}/>
                Pull from MFL / HIE</span>}
            footer={null} width={680}>

            <Alert style={{ marginBottom:16 }} type="warning" showIcon
                message="Requires MFL to be online and have coordinates populated"
                description="Once the national Master Facility List (MFL) has GPS coordinates, this will sync all facilities in one click. Until then, use Live GPS or Manual entry per facility."/>

            <Row gutter={12} style={{ marginBottom:12 }}>
                <Col span={16}>
                    <div style={{ fontWeight:600, fontSize:12, marginBottom:4 }}>MFL API URL</div>
                    <Input value={mflUrl} onChange={e => setMflUrl(e.target.value)}
                        placeholder="https://mfl.health.gov.sz/api/facilities"/>
                </Col>
                <Col span={8}>
                    <div style={{ fontWeight:600, fontSize:12, marginBottom:4 }}>API Key (if required)</div>
                    <Input.Password value={apiKey} onChange={e => setApiKey(e.target.value)}
                        placeholder="Bearer token..."/>
                </Col>
            </Row>

            <Button type="primary" icon={<CloudDownloadOutlined/>}
                loading={loading} onClick={pullFromMfl} style={{ marginBottom:16 }}>
                Pull Coordinates from MFL
            </Button>

            {loading && (
                <div style={{ textAlign:'center', padding:24 }}>
                    <Spin size="large"/>
                    <div style={{ marginTop:8, color:'#888', fontSize:12 }}>
                        Connecting to MFL...
                    </div>
                </div>
            )}

            {results && (
                <div>
                    <Row gutter={12} style={{ marginBottom:12 }}>
                        {[
                            { label:'Matched', val: results.matched?.length || 0, color:'#52c41a' },
                            { label:'Not in MFL', val: results.unmatched?.length || 0, color:'#fa8c16' },
                            { label:'No coords in MFL', val: results.no_coords?.length || 0, color:'#f5222d' },
                        ].map(s => (
                            <Col span={8} key={s.label}>
                                <Card size="small" style={{ textAlign:'center', borderTop:`3px solid ${s.color}` }}>
                                    <div style={{ fontSize:11, color:'#888' }}>{s.label}</div>
                                    <div style={{ fontSize:22, fontWeight:800, color:s.color }}>{s.val}</div>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {results.matched?.length > 0 && (
                        <div style={{ marginBottom:12 }}>
                            <div style={{ fontWeight:700, fontSize:12, marginBottom:6 }}>
                                Facilities to be updated:
                            </div>
                            <div style={{ maxHeight:200, overflowY:'auto', background:'#f6ffed',
                                border:'1px solid #b7eb8f', borderRadius:8, padding:'8px 12px' }}>
                                {results.matched.map(f => (
                                    <div key={f.id} style={{ fontSize:12, padding:'3px 0',
                                        borderBottom:'1px solid #e8e8e8', display:'flex', gap:12 }}>
                                        <span style={{ flex:1, fontWeight:600 }}>{f.business_name}</span>
                                        <span style={{ fontFamily:'monospace', color:'#389e0d' }}>
                                            {f.lat}, {f.lng}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <Button type="primary" icon={<CheckCircleOutlined/>}
                                loading={saving} onClick={applyAll}
                                style={{ marginTop:10, width:'100%', background:'#52c41a', borderColor:'#52c41a' }}>
                                Apply All {results.matched.length} Updates
                            </Button>
                        </div>
                    )}

                    {results.unmatched?.length > 0 && (
                        <Alert type="warning" showIcon style={{ marginTop:8 }}
                            message={`${results.unmatched.length} local facilities not found in MFL — use Live GPS or Manual for those`}/>
                    )}
                </div>
            )}
        </Modal>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function QoCFacilityCoordinates() {
    const [facilities, setFacilities] = useState([]);
    const [loading,    setLoading]    = useState(false);
    const [liveModal,  setLiveModal]  = useState(null);
    const [manModal,   setManModal]   = useState(null);
    const [mflModal,   setMflModal]   = useState(false);
    const [genQr,      setGenQr]      = useState(null);
    const [qrLoading,  setQrLoading]  = useState(false);
    const [qrHtml,     setQrHtml]     = useState('');

    // ── Filters ── Region, Facility Level, and Coordinate Source. Applied to
    // the full facilities list BEFORE it's split into withCoords/withoutCoords,
    // so both tables below stay consistent with whatever's selected. A
    // Coordinate Source filter naturally yields zero rows in the "missing
    // coordinates" table, since those never had a source to begin with —
    // that's correct, not a bug, so no special-casing is needed for it.
    const [filters, setFilters] = useState({ region: null, site_type: null, coord_source: null });

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await FacilityService.all({ status: 1 });
            // Normalize coordinate columns — prefer lat/lng (10,7), fall back to latitude/longitude
            const facs = (res.data?.result?.data || []).map(f => ({
                ...f,
                lat: f.lat || f.latitude  || null,
                lng: f.lng || f.longitude || null,
            }));
            setFacilities(facs);
        } catch { message.error('Failed to load facilities'); }
        finally { setLoading(false); }
    };

    const handleGenerateQr = async (facility) => {
        setGenQr(facility);
        setQrLoading(true);
        setQrHtml('');
        try {
            const res = await QoCService.getFacilityQrData(facility.id);
            setQrHtml(res.data?.qr_image_url || '');
        } catch { message.error('QR generation failed'); }
        finally { setQrLoading(false); }
    };

    const printQr = async (facility) => {
        // Simple hash — works on HTTP (no crypto.subtle needed)
        // Must match the backend QocQrGenerator SECRET
        const QR_SECRET = 'qoc-moh-sz-2025';
        const facCode   = facility.fac_code || ('FAC-' + facility.id);
        const timestamp = Math.floor(Date.now() / 1000);
        const msg       = facCode + ':' + timestamp + ':' + QR_SECRET;
        // FNV-1a 32-bit hash — simple, no crypto API needed
        let h = 2166136261;
        for (let i = 0; i < msg.length; i++) {
            h ^= msg.charCodeAt(i);
            h = (h * 16777619) >>> 0;
        }
        // Extend to 64 chars by hashing segments
        let token = '';
        for (let seed = 0; seed < 8; seed++) {
            let t = h ^ (seed * 2654435761);
            t = ((t >>> 16) ^ t) * 0x45d9f3b;
            t = ((t >>> 16) ^ t) * 0x45d9f3b;
            t = (t >>> 16) ^ t;
            token += (t >>> 0).toString(16).padStart(8, '0');
        }

        const base = window.location.hostname.includes('qoctesting') ? '/qoctesting' : '/qoc';
        const qrUrl = window.location.origin + base + '/qoc/survey'
            + '?fac='  + encodeURIComponent(facCode)
            + '&tok='  + token
            + '&t='    + timestamp
            + '&id='   + facility.id
            + '&name=' + encodeURIComponent(facility.business_name || '')
            + '&region=' + encodeURIComponent((facility.located_region || facility.region || '').replace(/[^\w\s-]/g, ''))
            + (facility.lat ? '&lat=' + facility.lat : '')
            + (facility.lng ? '&lng=' + facility.lng : '');

        // Use qrserver.com to generate the QR image (free, no API key)
        const qrImgUrl = 'https://api.qrserver.com/v1/create-qr-code/?size=250x250&ecc=M&data='
            + encodeURIComponent(qrUrl);

        // Get TinyURL for the poster short link
        let shortUrl = qrUrl;
        try {
            const tinyRes = await fetch(
                `https://tinyurl.com/api-create.php?url=${encodeURIComponent(qrUrl)}`,
                { signal: AbortSignal.timeout(5000) }
            );
            if (tinyRes.ok) {
                const tiny = await tinyRes.text();
                if (tiny.startsWith('https://tinyurl.com/')) shortUrl = tiny.trim();
            }
        } catch(e) { /* TinyURL failed — use full URL */ }

        // Convert local image to base64 for embedding in the print window
        const toBase64 = (url) => new Promise(resolve => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const cv = document.createElement('canvas');
                cv.width = img.width; cv.height = img.height;
                cv.getContext('2d').drawImage(img, 0, 0);
                resolve(cv.toDataURL('image/png'));
            };
            img.onerror = () => resolve('');
            img.src = url;
        });

        // Import the images via dynamic fetch since they are bundled assets
        const coaB64  = await toBase64(window.location.origin + (window.location.hostname.includes('qoctesting') ? '/qoctesting' : '/qoc') + '/web/build/' + 'static/media/' + coa.split('/').pop());
        const flagB64 = await toBase64(window.location.origin + (window.location.hostname.includes('qoctesting') ? '/qoctesting' : '/qoc') + '/web/build/' + 'static/media/' + flag.split('/').pop());

        const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>QR Code — ${(facility.business_name || '').replace(/</g,'&lt;')}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: Arial, sans-serif; background:#f0f4f8;
         display:flex; align-items:center; justify-content:center; min-height:100vh; }
  .card {
    background:white; border-radius:16px; padding:28px 28px 20px;
    text-align:center; max-width:400px; width:100%;
    box-shadow:0 4px 20px rgba(0,0,0,0.15);
    border:3px solid #003580;
    position:relative; overflow:hidden;
  }
  .flag-bar {
    height:8px; width:100%; margin:-28px -28px 20px;
    width:calc(100% + 56px);
    background: linear-gradient(to right,
      #3E5EB9 33.3%, #FFD700 33.3% 66.6%, #B10C0C 66.6%);
  }
  .header { display:flex; align-items:center; justify-content:center; gap:14px; margin-bottom:12px; }
  .header img.coa  { height:52px; }
  .header img.flag { height:36px; border-radius:3px; box-shadow:0 1px 4px rgba(0,0,0,0.2); }
  .moh   { font-size:10px; letter-spacing:2px; color:#666; text-transform:uppercase; margin-bottom:2px; }
  .title { font-size:18px; font-weight:800; color:#003580; margin-bottom:2px; }
  .badge { display:inline-block; background:#003580; color:#fff; font-size:10px;
           font-weight:700; padding:3px 10px; border-radius:20px; margin-bottom:14px; }
  .fac   { font-size:17px; font-weight:800; color:#1a1a2e; margin:10px 0 2px; }
  .meta  { font-size:12px; color:#888; margin-bottom:16px; }
  .qr    { margin:12px 0; }
  .qr img { border:4px solid #003580; border-radius:12px; }
  .instruction { font-size:13px; color:#444; line-height:1.7; margin:14px 0 10px; }
  .short-url {
    background:#f0f4ff; border:1px solid #c7d7f9; border-radius:8px;
    padding:8px 12px; margin:10px 0; font-size:13px; color:#003580;
    font-weight:700; letter-spacing:0.5px;
  }
  .short-label { font-size:10px; color:#888; margin-bottom:3px; }
  .footer { font-size:10px; color:#bbb; margin-top:14px; border-top:1px solid #eee; padding-top:10px; }
  .flag-bar-bottom {
    height:6px; width:calc(100% + 56px); margin:14px -28px -20px;
    background: linear-gradient(to right,
      #3E5EB9 33.3%, #FFD700 33.3% 66.6%, #B10C0C 66.6%);
  }
  @media print {
    body { background:white; }
    .no-print { display:none; }
  }
</style>
</head>
<body>
<div class="card">
  <div class="flag-bar"></div>
  <div class="header">
    ${coaB64 ? `<img class="coa" src="${coaB64}" alt="Coat of Arms"/>` : ''}
    <div>
      <div class="moh">Kingdom of Eswatini · Ministry of Health</div>
      <div class="title">Patient Satisfaction Survey</div>
    </div>
    ${flagB64 ? `<img class="flag" src="${flagB64}" alt="Flag"/>` : ''}
  </div>
  <div class="badge">National Quality Management Programme</div>
  <div class="fac">${(facility.business_name || '').replace(/</g,'&lt;')}</div>
  <div class="meta">${(facility.site_type || '').replace(/</g,'&lt;')}${facility.located_region ? ' · ' + facility.located_region : ''}</div>
  <div class="qr"><img src="${qrImgUrl}" width="220" height="220" alt="QR Code"/></div>
  <div class="instruction">
    <strong>Scan with your phone camera</strong><br/>
    Share your experience with us.<br/>
    Your feedback is <strong>anonymous</strong> and helps us improve healthcare.
  </div>
  ${shortUrl !== qrUrl ? `
  <div class="short-label">Or type this short link on your phone:</div>
  <div class="short-url">${shortUrl}</div>
  ` : ''}
  <div class="flag-bar-bottom"></div>
  <div class="no-print" style="margin-top:16px;">
    <button onclick="window.print()"
      style="background:#003580;color:#fff;border:none;padding:10px 28px;
             border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;margin-right:8px;">
        Print / Save as PDF
    </button>
    <button onclick="navigator.clipboard.writeText('${shortUrl}').then(()=>alert('Link copied!'))"
      style="background:#f0f4ff;color:#003580;border:2px solid #003580;padding:10px 16px;
             border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;">
        Copy Link
    </button>
  </div>
</div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (win) {
            win.document.write(html);
            win.document.close();
            trailUpdate(TRAIL_MODULES.FACILITIES, facility.business_name, facility.id,
                { action: 'QR code printed', facilityCode: facility.fac_code || 'FAC-' + facility.id });
        }else {
            message.error('Pop-up blocked — please allow pop-ups for this site');
        }
    };

    // Filters applied once, before splitting into with/without coordinates —
    // both tables below stay consistent with whatever's selected.
    const filtered = facilities.filter(f =>
        (!filters.region       || (f.located_region || f.region) === filters.region) &&
        (!filters.site_type    || f.site_type === filters.site_type) &&
        (!filters.coord_source || f.coord_source === filters.coord_source)
    );

    const withCoords    = filtered.filter(f => f.lat && f.lng);
    const withoutCoords = filtered.filter(f => !f.lat || !f.lng);

    const hasActiveFilters = filters.region || filters.site_type || filters.coord_source;

    const columns = [
        {
            title: 'Facility', dataIndex: 'business_name', width: 220,
            render: (n, r) => (
                <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{n}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{r.site_type} · {r.located_region}</div>
                </div>
            ),
        },
        {
            title: 'Coordinates', width: 200,
            render: (_, r) => {
                if (!r.lat || !r.lng) return (
                    <Tag color="error" style={{ fontSize: 11 }}>
                        <WarningOutlined/> No coordinates
                    </Tag>
                );
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div>
                            <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#389e0d', fontWeight: 700 }}>
                                {parseFloat(r.lat).toFixed(5)}, {parseFloat(r.lng).toFixed(5)}
                            </div>
                            {r.coord_source && (
                                <Tag style={{ fontSize: 10, marginTop: 2 }} color={
                                    r.coord_source === 'live_gps' ? 'green'
                                    : r.coord_source === 'mfl'    ? 'purple'
                                    : 'blue'
                                }>
                                    {r.coord_source === 'live_gps' ? 'Live GPS'
                                     : r.coord_source === 'mfl'    ? 'MFL'
                                     : 'Manual'}
                                    {r.coord_accuracy && ` ±${r.coord_accuracy}m`}
                                </Tag>
                            )}
                        </div>
                        <MapThumb lat={r.lat} lng={r.lng} size={56}/>
                    </div>
                );
            },
        },
        {
            title: 'QR Code', width: 90,
            render: (_, r) => {
                if (!r.lat || !r.lng) return (
                    <Tooltip title="Set coordinates first">
                        <Button size="small" icon={<QrcodeOutlined/>} disabled>QR</Button>
                    </Tooltip>
                );
                return (
                    <Button size="small" icon={<PrinterOutlined/>}
                        style={{ borderColor: '#722ed1', color: '#722ed1' }}
                        onClick={() => printQr(r)}>
                        Print
                    </Button>
                );
            },
        },
        {
            title: 'Set Coordinates', width: 220,
            render: (_, r) => (
                <div style={{ display: 'flex', gap: 6 }}>
                    <Tooltip title="Use this device's GPS — stand at the facility entrance">
                        <Button size="small" type="primary" icon={<AimOutlined/>}
                            style={{ background: '#52c41a', borderColor: '#52c41a' }}
                            onClick={() => setLiveModal(r)}>
                            Live GPS
                        </Button>
                    </Tooltip>
                    <Tooltip title="Paste coordinates from Google Maps">
                        <Button size="small" icon={<EditOutlined/>}
                            onClick={() => setManModal(r)}>
                            Manual
                        </Button>
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Facility Coordinates</h2>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                        GPS coordinates are embedded in QR codes to enforce on-site survey completion
                    </div>
                </div>
                <div className="my-auto ml-auto" style={{ display: 'flex', gap: 8 }}>
                    <Button icon={<CloudDownloadOutlined/>}
                        style={{ borderColor: '#722ed1', color: '#722ed1' }}
                        onClick={() => setMflModal(true)}>
                        Pull from MFL / HIE
                    </Button>
                    <Button icon={<ReloadOutlined/>} onClick={load} loading={loading}>
                        Refresh
                    </Button>
                </div>
            </div>

            <div className="page-pad">
                {/* Filters */}
                <Card size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={12} align="middle">
                        <Col span={6}>
                            <Select allowClear placeholder="Region" style={{ width: '100%' }}
                                value={filters.region || undefined}
                                onChange={v => setFilters(f => ({ ...f, region: v || null }))}>
                                {[...new Set(facilities.map(f => f.located_region || f.region).filter(Boolean))].sort().map(r => (
                                    <Select.Option key={r} value={r}>{r}</Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={6}>
                            <Select allowClear placeholder="Facility Level" style={{ width: '100%' }}
                                value={filters.site_type || undefined}
                                onChange={v => setFilters(f => ({ ...f, site_type: v || null }))}>
                                {FACILITY_LEVELS.map(l => (
                                    <Select.Option key={l} value={l}>{l}</Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={6}>
                            <Select allowClear placeholder="Coordinate Source" style={{ width: '100%' }}
                                value={filters.coord_source || undefined}
                                onChange={v => setFilters(f => ({ ...f, coord_source: v || null }))}>
                                <Select.Option value="live_gps">Live GPS</Select.Option>
                                <Select.Option value="manual">Manual</Select.Option>
                                <Select.Option value="mfl">MFL</Select.Option>
                            </Select>
                        </Col>
                        <Col span={3}>
                            {hasActiveFilters && (
                                <Button onClick={() => setFilters({ region: null, site_type: null, coord_source: null })}>
                                    Clear
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card>

                {/* Summary cards */}
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={6}>
                        <Card size="small" style={{ borderTop: '3px solid #52c41a', textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: '#888' }}>With Coordinates</div>
                            <div style={{ fontSize: 28, fontWeight: 800, color: '#52c41a' }}>{withCoords.length}</div>
                            <div style={{ fontSize: 11, color: '#888' }}>QR codes ready to print</div>
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card size="small" style={{ borderTop: '3px solid #f5222d', textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: '#888' }}>Missing Coordinates</div>
                            <div style={{ fontSize: 28, fontWeight: 800, color: '#f5222d' }}>{withoutCoords.length}</div>
                            <div style={{ fontSize: 11, color: '#888' }}>Need GPS capture</div>
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card size="small" style={{ borderTop: '3px solid #1890ff', textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: '#888' }}>
                                {hasActiveFilters ? 'Matching Facilities' : 'Total Facilities'}
                            </div>
                            <div style={{ fontSize: 28, fontWeight: 800, color: '#1890ff' }}>{filtered.length}</div>
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card size="small" style={{ borderTop: '3px solid #722ed1', textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: '#888' }}>Coverage</div>
                            <div style={{ fontSize: 28, fontWeight: 800, color: '#722ed1' }}>
                                {filtered.length ? Math.round(withCoords.length / filtered.length * 100) : 0}%
                            </div>
                            <Progress percent={filtered.length ? Math.round(withCoords.length / filtered.length * 100) : 0}
                                showInfo={false} size="small" strokeColor="#722ed1"/>
                        </Card>
                    </Col>
                </Row>

                {/* Method guide */}
                <Alert style={{ marginBottom: 16 }} type="info" showIcon
                    message="Three ways to set coordinates"
                    description={
                        <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                            <strong>📍 Live GPS</strong> — Best accuracy. Take this device to the facility entrance and capture GPS on-site. Averages 10 readings for ±5–15m precision.<br/>
                            <strong>✏ Manual</strong> — Open Google Maps, right-click the facility entrance, copy the coordinates that appear and paste them here.<br/>
                            <strong>🌐 MFL / HIE</strong> — When the national Master Facility List has coordinates, click "Pull from MFL" to sync all facilities in one click.
                        </div>
                    }/>

                {/* Facilities missing coords first */}
                {withoutCoords.length > 0 && (
                    <Card size="small" style={{ marginBottom: 16, borderLeft: '3px solid #f5222d' }}
                        title={<span style={{ color: '#f5222d' }}>
                            <WarningOutlined style={{ marginRight: 6 }}/>
                            {withoutCoords.length} facilities missing coordinates
                        </span>}>
                        <Table
                            size="small"
                            dataSource={withoutCoords}
                            columns={columns}
                            rowKey="id"
                            pagination={false}
                            loading={loading}/>
                    </Card>
                )}

                {/* Facilities with coords */}
                <Card size="small"
                    title={<span style={{ color: '#52c41a' }}>
                        <CheckCircleOutlined style={{ marginRight: 6 }}/>
                        {withCoords.length} facilities ready — QR codes can be printed
                    </span>}>
                    <Table
                        size="small"
                        dataSource={withCoords}
                        columns={columns}
                        rowKey="id"
                        pagination={{ pageSize: 20 }}
                        loading={loading}/>
                </Card>
            </div>

            {/* Modals */}
            {liveModal && (
                <LiveGpsModal facility={liveModal}
                    onSave={() => { setLiveModal(null); load(); }}
                    onClose={() => setLiveModal(null)}/>
            )}
            {manModal && (
                <ManualModal facility={manModal}
                    onSave={() => { setManModal(null); load(); }}
                    onClose={() => setManModal(null)}/>
            )}
            {mflModal && (
                <MflModal facilities={facilities}
                    onSave={() => { setMflModal(false); load(); }}
                    onClose={() => setMflModal(false)}/>
            )}
        </div>
    );
}
